<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">Register Quality</v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mt-3 mx-1">
                <v-col cols="12" class="d-flex align-center pb-3">
                    <div class="d-flex align-center">
                        <v-icon class="pr-2">mdi-database-outline</v-icon>
                        <p class="mb-0">
                            Pending For Review:
                            {{ selectedProcess?.pendingQty || 0 }}
                        </p>
                    </div>
                </v-col>
                <v-col cols="12" class="pt-0 pb-1">
                    <v-combobox
                        v-model="selectedProcess"
                        :items="availableProcesses"
                        item-text="name"
                        item-value="id"
                        label="Process"
                        required
                        :rules="[rules.required]"
                        @change="destinationProcess = undefined"
                    >
                    </v-combobox>
                </v-col>
                <v-col
                    cols="12"
                    class="pt-0 pb-1"
                    v-if="nextProcess?.id === 'quality'"
                >
                    <v-text-field
                        v-model="releaseQty"
                        prepend-icon="mdi-check"
                        color="green"
                        label="Amount to release:"
                        type="text"
                        required
                        :rules="[maxValue()]"
                        class="ma-0"
                    />
                </v-col>
                <v-col cols="12" class="pt-0 pb-1">
                    <v-text-field
                        v-model="reprocessQty"
                        prepend-icon="mdi-sync"
                        color="orange"
                        label="amount to be reprocessed:"
                        type="text"
                        required
                        :rules="[maxValue()]"
                        class="ma-0"
                    />
                </v-col>
                <div v-if="reprocessQty > 0 && previousProcesses.length > 0">
                    <v-col cols="12" class="pt-0 pb-1">
                        <v-combobox
                            v-model="reprocessReason"
                            :items="ncCodes"
                            hide-selected
                            flat
                            item-text="longName"
                            label="Reprocess Reason"
                            prepend-icon="mdi-barcode"
                            required
                            :rules="[rules.required]"
                        >
                        </v-combobox>
                    </v-col>
                    <v-col cols="12" class="pt-0 pb-1">
                        <v-combobox
                            v-model="destinationProcess"
                            :items="previousProcesses"
                            item-text="name"
                            item-value="id"
                            label="Return to"
                            prepend-icon="mdi-tools"
                            :disabled="previousProcesses.length <= 0"
                            required
                            :rules="[rules.required]"
                        >
                        </v-combobox>
                    </v-col>
                </div>
                <v-col cols="12" class="pt-0">
                    <v-text-field
                        v-model="rejectQty"
                        prepend-icon="mdi-close"
                        color="red"
                        label="amount to be rejected:"
                        type="text"
                        required
                        :rules="[maxValue()]"
                        class="ma-0"
                    />
                </v-col>
                <v-col cols="12" class="pt-0 ma-0" v-if="rejectQty">
                    <v-combobox
                        v-model="NCcode"
                        :items="ncCodes"
                        hide-details
                        prepend-icon="mdi-barcode"
                        hide-selected
                        label="Non-conformity code: "
                        required
                        flat
                        item-text="longName"
                        :rules="[rules.required]"
                        class="mt-n3"
                    />
                </v-col>
            </v-form>
        </v-card-text>
        <v-card-actions class="pb-3 pt-0">
            <v-btn text color="secondary" @click="close">
                Close
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" :disabled="!valid" @click="save">
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'

export default {
    name: 'RegisterQualityPerProcess',
    props: {
        item: {
            type: Object,
            required: true,
        },
        workOrder: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },

    data: () => ({
        valid: false,
        loading: false,
        releaseQty: undefined,
        reprocessQty: undefined,
        rejectQty: undefined,
        NCcode: undefined,
        pendingQty: 0,
        rules: {
            required: v => !!v || 'Required',
            positive: v => (v && v <= 0) || 'Only positive values',
        },
        ncCodes: [],
        qualityProcess: { name: 'Quality', id: 'quality' },
        packingProcessId: 'packing',
        settingProcessesId: '',
        processDefinitions: [],
        rejectionReasons: [],
        selectedProcess: undefined,
        reprocessReason: undefined,
        destinationProcess: undefined,
    }),
    async mounted() {
        try {
            this.loading = true
            // get settings
            const {
                data: { settings },
            } = await API.getSettings()
            const settingProcesses = settings.find(
                setting => setting.name.toLowerCase() == 'processes'
            )
            if (settingProcesses) {
                this.settingProcessesId = settingProcesses.id
                this.processDefinitions = settingProcesses.processes || []
            }

            const setting = settings.find(
                setting => setting.name.toLowerCase() == 'quality'
            )

            // get nc codes
            this.ncCodes = setting.ncCodes
            this.ncCodes.forEach(code => {
                code.longName = code.description
                    ? `${code.code} - (${code.description})`
                    : `${code.code}`
            })
            // get rejection reasons
            this.rejectionReasons = setting?.rejectionReasons || []
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        processes() {
            const orderedProcessIds = Array.isArray(this.item?.processes)
                ? this.item.processes
                : []

            if (orderedProcessIds.length === 0) {
                return []
            }

            const productionEntries = Array.isArray(this.item?.production)
                ? this.item.production
                : []
            const qualityEntries = Array.isArray(this.item?.quality)
                ? this.item.quality
                : []

            return orderedProcessIds
                .map(processId => {
                    const totalProduced = productionEntries
                        .filter(entry => entry.process?.id === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.qty || 0),
                            0
                        )
                    const totalReleased = qualityEntries
                        .filter(entry => entry.selectedProcessId === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.releaseQty || 0),
                            0
                        )
                    const totalRejected = qualityEntries
                        .filter(entry => entry.selectedProcessId === processId)
                        .reduce(
                            (accumulator, entry) =>
                                accumulator + Number(entry.rejectQty || 0),
                            0
                        )
                    const pendingQty =
                        totalProduced - totalReleased - totalRejected

                    if (processId === this.qualityProcess.id) {
                        return {
                            id: this.qualityProcess.id,
                            name: this.qualityProcess.name,
                            assemblyProcess: false,
                            pendingQty,
                        }
                    }

                    const targetProcess = this.processDefinitions.find(
                        process => process.id === processId
                    )

                    if (!targetProcess) {
                        return undefined
                    }

                    return {
                        id: targetProcess.id,
                        name: targetProcess.name,
                        assemblyProcess: targetProcess.assemblyProcess,
                        pendingQty,
                    }
                })
                .filter(
                    process => process && process.id !== this.packingProcessId
                )
        },
        availableProcesses() {
            return this.processes.filter(
                process => process.id !== this.qualityProcess.id
            )
        },
        previousProcesses() {
            if (!this.selectedProcess?.id) {
                return []
            }

            const orderedProcessIds = Array.isArray(this.item.processes)
                ? this.item.processes
                : []
            const selectedIndex = orderedProcessIds.findIndex(
                processId => processId === this.selectedProcess.id
            )

            if (selectedIndex < 0) {
                return []
            }

            const previousIds = orderedProcessIds.slice(0, selectedIndex + 1)

            return previousIds
                .map(processId =>
                    this.processes.find(process => process.id === processId)
                )
                .filter(Boolean)
        },
        nextProcess() {
            if (!this.selectedProcess?.id) {
                return undefined
            }

            const orderedProcessIds = Array.isArray(this.item.processes)
                ? this.item.processes
                : []
            const selectedIndex = orderedProcessIds.findIndex(
                processId => processId === this.selectedProcess.id
            )

            if (
                selectedIndex < 0 ||
                selectedIndex >= orderedProcessIds.length - 1
            ) {
                return undefined
            }

            const nextProcessId = orderedProcessIds[selectedIndex + 1]

            return this.processes.find(process => process.id === nextProcessId)
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        resetFormState() {
            this.valid = false
            this.releaseQty = undefined
            this.reprocessQty = undefined
            this.rejectQty = undefined
            this.NCcode = undefined
            this.selectedProcess = undefined
            this.reprocessReason = undefined
            this.destinationProcess = undefined
        },
        close() {
            this.resetFormState()
            this.$emit('closeDialog')
        },
        async save() {
            try {
                this.loading = true
                let params = {
                    partNumberId: this.item.partNumberId,
                    workOrderId: this.workOrder.id,
                    selectedProcess: this.selectedProcess.id,
                }

                if (this.releaseQty) {
                    params.releaseQty = Number(this.releaseQty)
                }
                if (this.reprocessQty) {
                    params.reprocessQty = Number(this.reprocessQty)
                    params.reprocessReason = this.reprocessReason.id
                    params.destinationProcess = this.destinationProcess.id
                }
                if (this.rejectQty) {
                    params.rejectQty = Number(this.rejectQty)
                    params.NCcode = this.NCcode.id
                }

                params.settingId = this.settingProcessesId

                const items = await API.registerProcessQuality(params)
                this.resetFormState()
                this.$emit('closeDialog', items)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        maxValue() {
            const keys = ['releaseQty', 'reprocessQty', 'rejectQty']

            for (const key of keys) {
                const value = this[key]

                if (value !== undefined && value !== null && value !== '') {
                    const numValue = Number(value)
                    if (isNaN(numValue)) {
                        return `must be a valid number.`
                    }
                    if (numValue < 0) {
                        return `cannot be negative.`
                    }
                }
            }

            const total =
                Number(this.releaseQty || 0) +
                Number(this.reprocessQty || 0) +
                Number(this.rejectQty || 0)

            if (total > this.selectedProcess?.pendingQty) {
                return `The sum of all the quantities cannot be greater than ${this.selectedProcess?.pendingQty}.`
            } else {
                return true
            }
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}
</style>
